import React, { useRef, useState } from 'react';
import { 
  Download, 
  Printer, 
  Copy, 
  Check, 
  ShieldCheck, 
  Sparkles, 
  FileText, 
  TrendingUp, 
  Lock, 
  Layers, 
  DollarSign, 
  Users, 
  Building2, 
  ExternalLink,
  Award,
  Globe,
  CheckCircle2,
  Calendar
} from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas-pro';
import { sanitizeClonedDocForPdf } from '../utils/pdfColorSanitizer';

interface GrindstoneOnePagerProps {
  onClose?: () => void;
  showToast?: (msg: string, type?: 'success' | 'info') => void;
}

export function GrindstoneOnePager({ onClose, showToast }: GrindstoneOnePagerProps) {
  const [copied, setCopied] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const onePagerRef = useRef<HTMLDivElement>(null);

  const plainTextBriefing = `AZURAOS — EXECUTIVE VENTURE BRIEFING
Target Evaluator: Thandiwe Maqetuka CA(SA), Investment Manager, Knife Capital / Grindstone
Company: AzuraOS (Azura Seraph™ Pty Ltd)
Founder & Architect: Raksha Singh (founder@azuraos.app)
Live Production URL: https://azuraos.app
Status: Live Production • Google for Startups Cloud Program (Start Tier Recipient)
Grindstone Alignment: 60+ Question Diligence Intake Completed

1. EXECUTIVE SUMMARY
AzuraOS is the Sovereign Financial Operating Engine for autonomous founders, high-output consultancies, and lean tech SMEs. We counter the SaaSpocalypse by replacing 8–14 disconnected tools (costing $300–$800/mo) with a single deterministic, un-promptable state machine that unifies quote-to-cash invoicing, real-time margin defense, automated statutory tax reserves, and our proprietary 0.05s AI Bank Reconciliation Engine with 100% margin BYOK architecture.

2. THE PROBLEM: THE COLLAPSE OF 'FEATURE SAAS' & REACTIVE ACCOUNTING
- Feature SaaS Obsolescence: Generic software tools are dying because AI easily replicates point features, yet charging solo operators $200+/month across 6 disconnected tools.
- Reactive Bookkeeping: Traditional accounting looks in the rearview mirror; founders only discover tax liabilities and cashflow holes months later.
- Centralized Token Burn Trap: SaaS platforms attempting to retrofit AI suffer crushed gross margins (collapsing from 85% to 35%) due to continuous server-side inference bills.

3. THE FINANCIAL ENGINE & RECONCILIATION SUITE (BUILT FOR AUDIT READINESS)
- Real-Time Margin Radar: Sub-second net margin recalculation with automated pre-tax reserve calculations (SARS 15% VAT, Provisional Income Tax, HMRC, IRS) held aside *before* commitments occur.
- 0.05s Air-Gapped Reconciler: Ingests CSV/PDF bank statements in-memory with zero password scraping liability. Executes intelligent matching, multi-currency FX normalization, and 1-click Nil Return packs.
- Unbroken Contract-to-Settlement Pipeline: Multi-currency quote-to-cash workflows, instant PDF agreements, and synchronized client booking calendars (/book).
- 100% Margin Stateless BYOK Architecture: Ephemeral Gemini API integration directly with user keys. Zero central inference burn, protecting 90%+ gross margins.

4. SECURITY, GOVERNANCE & REGULATORY MOAT
- Client-Side AES-256-GCM Sovereign Vaults: Zero corporate telemetry. Proprietary financial ledgers and customer data are encrypted client-side.
- POPIA & GDPR Compliance: Sovereign data stewardship with automated single-click cryptographic data shredder.
- Tier-1 Google Cloud Posture: Hosted on Google Cloud Run (Europe-West1), inheriting Google Cloud SOC 1/2/3, ISO 27001, and PCI-DSS Level 1 compliance. Member of Google Cloud Partner Advantage.
- Global Merchant of Record (Dodo Payments): Handles multi-currency tokenized checkouts and automated sales tax/VAT remittance across 150+ jurisdictions.
- Non-Custodial Moat: Zero client fund custody and zero Open Banking licensing liabilities; 100% pure software leverage.

5. BUSINESS MODEL & UNIT ECONOMICS
- Pre-Launch Wedge: $29/mo ($290/yr) Ungated Core Engine with 7-day trial.
- Expansion Tracks: Sovereign AI Co-Founder ($19/mo), Team Multi-Seat ($79/mo), Institutional Cohort Licensing.
- Defended Gross Margins: 90%+ due to local-first client computing and BYOK AI inference.
- Post-Seed Expansion: Pricing steps up to $79–$99/mo for new cohorts as product maturity and automated integrations scale.
- B2B Enterprise Cohort Licensing: Packaged institutional seats for accelerators, ESD supplier diversity programs, and university incubators.
- Gross Margin Profile: >90% software gross margins via serverless Cloud Run infrastructure.

6. THE ASK & MILESTONES
- The Ask: $250,000 – $500,000 Strategic Pre-Seed Capital.
- Use of Funds: 2 Strategic Key Hires (Enterprise/Cohort Partnerships Lead to capture accelerator pipelines + Full-Stack Systems Engineer) to scale distribution while maintaining ultra-lean burn.
- 12-Month Target: Scale to 500+ paying businesses, secure 3 institutional cohort pilots, and achieve $1.2M ARR run-rate.

Contact: Raksha Singh • Founder & CEO • founder@azuraos.app • Live System: https://azuraos.app • Azura Seraph™ Pty Ltd`;

  const handleCopyText = () => {
    navigator.clipboard.writeText(plainTextBriefing);
    setCopied(true);
    if (showToast) showToast('📋 Grindstone Executive Briefing copied to clipboard!', 'success');
    setTimeout(() => setCopied(false), 3000);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPdf = async () => {
    setIsGeneratingPdf(true);
    try {
      // Always target the calibrated single-page A4 canvas to guarantee 100% fit on 1 A4 page with zero cuts
      const exportElement = document.getElementById('grindstone-a4-pdf-canvas');
      if (!exportElement) {
        throw new Error('Export canvas element not found');
      }

      const canvas = await html2canvas(exportElement, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        onclone: (clonedDoc) => {
          sanitizeClonedDocForPdf(clonedDoc);
          const clonedEl = clonedDoc.getElementById('grindstone-a4-pdf-canvas');
          if (clonedEl) {
            clonedEl.style.position = 'static';
            clonedEl.style.left = '0';
            clonedEl.style.top = '0';
            clonedEl.style.display = 'flex';
            clonedEl.style.visibility = 'visible';
            clonedEl.style.opacity = '1';
          }
          const wrapper = clonedDoc.getElementById('grindstone-a4-pdf-canvas-wrapper');
          if (wrapper) {
            wrapper.style.position = 'static';
            wrapper.style.left = '0';
            wrapper.style.top = '0';
            wrapper.style.display = 'block';
            wrapper.style.visibility = 'visible';
            wrapper.style.opacity = '1';
            wrapper.style.overflow = 'visible';
          }
        }
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      // Fit strictly on a single A4 page (210mm x 297mm) with zero slicing or cuts
      pdf.addImage(imgData, 'PNG', 0, 0, 210, 297, undefined, 'FAST');

      pdf.save('AzuraOS_Grindstone_Executive_One_Pager_Raksha_Singh.pdf');
      if (showToast) showToast('✅ 1-Page Grindstone PDF downloaded successfully!', 'success');
    } catch (err) {
      console.error('PDF export failed:', err);
      if (showToast) showToast('⚠️ PDF export encountered an issue, opening print dialog instead.', 'info');
      window.print();
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* ACTION BAR */}
      <div className="flex flex-wrap items-center justify-between gap-3 p-3 sm:p-4 bg-[#151819] border border-[#2A2F31] rounded-2xl print:hidden">
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-xs font-mono font-bold text-white uppercase tracking-wider">
            Grindstone &amp; Knife Capital Tailored Briefing
          </span>
          <span className="hidden sm:inline-block px-2 py-0.5 text-[9px] font-mono font-extrabold uppercase bg-teal-500/20 text-teal-300 rounded border border-teal-500/40">
            CA(SA) &amp; VC Diligence Ready
          </span>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={handleCopyText}
            className="px-3 py-1.5 bg-[#0C0E0F] hover:bg-[#1F2426] text-gray-200 hover:text-white text-xs font-bold rounded-xl border border-[#2A2F31] transition flex items-center gap-1.5 cursor-pointer"
            title="Copy plain text to paste in email"
          >
            {copied ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
            <span>{copied ? 'Copied' : 'Copy Text'}</span>
          </button>

          <button
            onClick={handlePrint}
            className="px-3 py-1.5 bg-[#0C0E0F] hover:bg-[#1F2426] text-gray-200 hover:text-white text-xs font-bold rounded-xl border border-[#2A2F31] transition flex items-center gap-1.5 cursor-pointer"
            title="Print to printer or native PDF"
          >
            <Printer size={14} />
            <span>Print / Save as PDF</span>
          </button>

          <button
            onClick={handleDownloadPdf}
            disabled={isGeneratingPdf}
            className="px-3.5 py-1.5 bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-400 hover:to-emerald-400 text-black font-extrabold text-xs rounded-xl shadow-md transition flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
          >
            {isGeneratingPdf ? (
              <>
                <span className="w-3 h-3 border-2 border-black border-t-transparent rounded-full animate-spin" />
                <span>Exporting...</span>
              </>
            ) : (
              <>
                <Download size={14} />
                <span>Download PDF</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* PRINTABLE / RENDERED ONE-PAGER DOCUMENT (LIGHT THEME INSTITUTIONAL A4) */}
      <div 
        ref={onePagerRef}
        id="grindstone-one-pager-doc"
        className="bg-white text-slate-900 p-6 sm:p-10 rounded-2xl shadow-2xl border border-slate-200 space-y-6 max-w-4xl mx-auto font-sans leading-relaxed text-xs sm:text-sm print:p-0 print:border-none print:shadow-none print:max-w-none print:text-black"
        style={{ color: '#0f172a' }}
      >
        {/* TOP HEADER */}
        <div className="border-b-2 border-slate-900 pb-5 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="text-2xl sm:text-3xl font-black tracking-tight text-slate-900 font-mono">
                AZURA<span className="text-teal-600">OS</span>
              </span>
              <span className="text-[10px] font-mono font-bold px-2 py-0.5 bg-teal-50 text-teal-800 border border-teal-200 rounded uppercase">
                Venture One-Pager
              </span>
            </div>
            <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider font-mono">
              Sovereign Financial Operating Engine for Independent Operators &amp; Lean SMEs
            </p>
            <div className="text-[11px] text-slate-500">
              Company: <strong className="text-slate-800">Azura Seraph™ Pty Ltd</strong> • Founder &amp; Architect: <strong className="text-slate-800">Raksha Singh</strong> • <a href="mailto:founder@azuraos.app" className="text-teal-700 font-bold hover:underline">founder@azuraos.app</a>
            </div>
          </div>

          <div className="sm:text-right space-y-0.5 text-xs text-slate-600 font-mono">
            <div className="text-[10px] uppercase font-bold text-teal-700">CONFIDENTIAL EXECUTIVE BRIEFING</div>
            <div>Prepared for: <strong className="text-slate-900">Thandiwe Maqetuka CA(SA)</strong></div>
            <div>Knife Capital &amp; Grindstone Ventures</div>
            <div className="text-[10px] text-slate-500">September 2026 • Live Production</div>
          </div>
        </div>

        {/* STRATEGIC BADGES */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center text-[10px] font-mono font-bold uppercase">
          <div className="p-2 bg-slate-50 border border-slate-200 rounded-lg text-slate-700">
            <span className="text-teal-700 block text-xs font-black">Google Cloud</span>
            Startups Program (Start Tier)
          </div>
          <div className="p-2 bg-slate-50 border border-slate-200 rounded-lg text-slate-700">
            <span className="text-teal-700 block text-xs font-black">Zero Telemetry</span>
            AES-256-GCM Sovereign Vault
          </div>
          <div className="p-2 bg-slate-50 border border-slate-200 rounded-lg text-slate-700">
            <span className="text-teal-700 block text-xs font-black">Diligence Ready</span>
            60+ Question Grindstone Intake
          </div>
          <div className="p-2 bg-slate-50 border border-slate-200 rounded-lg text-slate-700">
            <span className="text-teal-700 block text-xs font-black">Global MoR</span>
            Dodo Payments (PCI-DSS L1)
          </div>
        </div>

        {/* 1. EXECUTIVE SUMMARY & PROBLEM */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-2">
            <h3 className="text-xs font-black text-slate-900 uppercase tracking-wider font-mono flex items-center gap-1.5 text-teal-700">
              <Sparkles size={14} />
              1. The Executive Thesis
            </h3>
            <p className="text-slate-700 text-[11px] leading-relaxed">
              AzuraOS replaces <strong>8 to 14 fragmented SaaS subscriptions</strong> with a single deterministic operating layer. Engineered for high-output solo operators, consulting practices, and growing tech SMEs, AzuraOS turns erratic freelancing into an institutional enterprise with automated margin defense, invoicing, and audit-ready reconciliation.
            </p>
          </div>

          <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-2">
            <h3 className="text-xs font-black text-slate-900 uppercase tracking-wider font-mono flex items-center gap-1.5 text-red-700">
              <TrendingUp size={14} />
              2. The Problem: SaaS Sprawl &amp; Reactive Accounting
            </h3>
            <ul className="text-slate-700 text-[11px] space-y-1 list-disc list-inside">
              <li><strong>28.4% Margin Erosion:</strong> Founders spend $300–$800/mo on disconnected tools with zero unified intelligence.</li>
              <li><strong>Rearview-Mirror Bookkeeping:</strong> Traditional tools only report past transactions, leaving founders blindsided by sudden VAT/income tax bills.</li>
              <li><strong>Telemetry Harvesting:</strong> Proprietary client proposals and balance sheets are stored unencrypted on corporate silos.</li>
            </ul>
          </div>
        </div>

        {/* 2. THE FINANCIAL & RECONCILIATION ENGINE (CA FOCUS) */}
        <div className="p-4 bg-teal-50/60 border border-teal-200 rounded-xl space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-black text-teal-900 uppercase tracking-wider font-mono flex items-center gap-1.5">
              <DollarSign size={14} className="text-teal-700" />
              3. The Financial Operating Engine &amp; AI Reconciliation Suite
            </h3>
            <span className="text-[10px] font-mono font-bold text-teal-800 bg-teal-100 px-2 py-0.5 rounded">
              Auditor &amp; CA(SA) Approved Flow
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-[11px]">
            <div className="bg-white p-3 rounded-lg border border-teal-100 shadow-sm space-y-1">
              <strong className="text-slate-900 block font-mono">Sub-Second Margin Radar</strong>
              <p className="text-slate-600">
                Calculates net profit margins in real-time. Automatically reserves provisional income tax and VAT (SARS 15%, HMRC, IRS) <em>before</em> expenses occur.
              </p>
            </div>
            <div className="bg-white p-3 rounded-lg border border-teal-100 shadow-sm space-y-1">
              <strong className="text-slate-900 block font-mono">AI Reconciliation Hub</strong>
              <p className="text-slate-600">
                Dual-pass bank statement (CSV) and receipt OCR matching with confidence scores. Automatic tax categorization and anomaly detection without bank scraping liability.
              </p>
            </div>
            <div className="bg-white p-3 rounded-lg border border-teal-100 shadow-sm space-y-1">
              <strong className="text-slate-900 block font-mono">1-Click Accountant Export</strong>
              <p className="text-slate-600">
                Produces comprehensive ledger summaries, VAT breakdown reports, and immutable audit trails ready for annual financial statements.
              </p>
            </div>
          </div>
        </div>

        {/* 3. SECURITY, GOVERNANCE & REGULATORY MOAT */}
        <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-2.5">
          <h3 className="text-xs font-black text-slate-900 uppercase tracking-wider font-mono flex items-center gap-1.5 text-emerald-800">
            <ShieldCheck size={14} className="text-emerald-700" />
            4. Enterprise Security, Privacy &amp; Regulatory Moat
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-[11px] text-slate-700">
            <div className="flex items-start gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-600 mt-1.5 shrink-0" />
              <div>
                <strong className="text-slate-900">Client-Side AES-256-GCM Sovereign Vaults:</strong> Zero corporate telemetry. Invoices, balance sheets, and client data are encrypted in the founder's browser.
              </div>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-600 mt-1.5 shrink-0" />
              <div>
                <strong className="text-slate-900">POPIA (South Africa) &amp; GDPR (Europe) Compliant:</strong> Sovereign local-first storage with instant 1-click cryptographic zeroization upon account closure.
              </div>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-600 mt-1.5 shrink-0" />
              <div>
                <strong className="text-slate-900">Google Cloud Partner Advantage:</strong> Hosted on Google Cloud Run (Europe-West1), inheriting SOC 1/2/3, ISO 27001, and PCI-DSS Level 1 compliance.
              </div>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-600 mt-1.5 shrink-0" />
              <div>
                <strong className="text-slate-900">Non-Custodial Regulatory Moat:</strong> Does not hold customer funds and avoids risky Open Banking credentials. 100% pure software leverage.
              </div>
            </div>
          </div>
        </div>

        {/* 4. BUSINESS MODEL, UNIT ECONOMICS & GRINDSTONE ALIGNMENT */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-[11px]">
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-2">
            <h3 className="text-xs font-black text-slate-900 uppercase tracking-wider font-mono flex items-center gap-1.5">
              <Layers size={14} className="text-indigo-600" />
              5. Unit Economics &amp; Pricing Wedge
            </h3>
            <ul className="space-y-1 text-slate-700">
              <li><strong>Pre-Launch Pro Wedge:</strong> $29/mo ($290/yr) 100% ungated engine following 7-day trial. Fast customer acquisition.</li>
              <li><strong>Post-Seed Price Expansion:</strong> $79–$99/mo for new cohorts as product integrations and automation mature.</li>
              <li><strong>Enterprise Cohorts:</strong> Bulk licenses for incubators, ESD supplier programs, and university innovation centers.</li>
              <li><strong>Gross Margins:</strong> &gt;90% software gross margin on serverless Cloud Run infrastructure.</li>
            </ul>
          </div>

          <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-2">
            <h3 className="text-xs font-black text-slate-900 uppercase tracking-wider font-mono flex items-center gap-1.5">
              <Award size={14} className="text-amber-600" />
              6. Grindstone Diligence Alignment
            </h3>
            <ul className="space-y-1 text-slate-700">
              <li><strong>60+ Question Intake Complete:</strong> Operational metrics, target market, and governance already structured.</li>
              <li><strong>Clean IP Ownership:</strong> 100% founder-architected and engineered codebase; zero agency or outsourced liabilities.</li>
              <li><strong>Capital Efficiency:</strong> Achieved live production and enterprise certifications with near-zero historical burn.</li>
              <li><strong>African &amp; Global Scale:</strong> Native support for ZAR, USD, EUR, GBP, and localized tax systems.</li>
            </ul>
          </div>
        </div>

        {/* 5. THE ASK & MILESTONES */}
        <div className="p-4 bg-slate-900 text-white rounded-xl space-y-2.5 print:bg-slate-900 print:text-white">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-700 pb-2">
            <div>
              <span className="text-[10px] font-mono text-teal-400 uppercase font-bold tracking-wider">THE STRATEGIC PRE-SEED ASK</span>
              <h4 className="text-base sm:text-lg font-black text-white">$250,000 – $500,000 Pre-Seed Capital</h4>
            </div>
            <div className="text-[11px] font-mono text-teal-300">
              High-Leverage 2-Person Strategic Core Hire
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-[11px]">
            <div className="p-2.5 bg-slate-800/80 rounded-lg border border-slate-700">
              <strong className="text-teal-300 block font-mono">1. Capital Allocation</strong>
              <p className="text-slate-300 text-[10px] mt-0.5">
                Funding 2 key strategic hires: Enterprise/Cohort Partnerships Lead (incubator/ESD pipelines) &amp; Full-Stack Systems Engineer.
              </p>
            </div>
            <div className="p-2.5 bg-slate-800/80 rounded-lg border border-slate-700">
              <strong className="text-teal-300 block font-mono">2. Near-Term Milestones</strong>
              <p className="text-slate-300 text-[10px] mt-0.5">
                500 active paying Pro subscribers, 3 cohort enterprise pilots, and complete regional market validation within 6 months.
              </p>
            </div>
            <div className="p-2.5 bg-slate-800/80 rounded-lg border border-slate-700">
              <strong className="text-teal-300 block font-mono">3. 12-Month Target</strong>
              <p className="text-slate-300 text-[10px] mt-0.5">
                Scale to $1.2M ARR run-rate at $79/mo expansion pricing, establishing AzuraOS as the global sovereign founder OS standard.
              </p>
            </div>
          </div>
        </div>

        {/* FOOTER & DIRECT CONTACT */}
        <div className="border-t border-slate-300 pt-3 flex flex-col sm:flex-row items-center justify-between gap-2 text-[10px] text-slate-500 font-mono">
          <div>
            <strong>Raksha Singh</strong> • Founder &amp; Architect • Azura Seraph™ Pty Ltd • <a href="mailto:founder@azuraos.app" className="text-teal-700 underline font-bold">founder@azuraos.app</a>
          </div>
          <div>
            AzuraOS Live Production: <a href="https://azuraos.app" target="_blank" rel="noopener noreferrer" className="text-teal-700 font-bold underline">https://azuraos.app</a>
          </div>
        </div>
      </div>

      {/* DEDICATED HIGH-RES SINGLE-PAGE A4 TARGET FOR PDF GENERATION & NATIVE PRINT (ZERO CUTS) */}
      <div
        id="grindstone-a4-pdf-canvas-wrapper"
        style={{
          position: 'absolute',
          left: '-9999px',
          top: 0,
          width: '794px',
          height: '1123px',
          overflow: 'hidden',
          zIndex: -9999,
          pointerEvents: 'none'
        }}
        aria-hidden="true"
      >
        <div
          id="grindstone-a4-pdf-canvas"
          style={{
            width: '794px',
            height: '1123px',
            maxHeight: '1123px',
            backgroundColor: '#ffffff',
            color: '#0f172a',
            padding: '24px 30px',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
          }}
        >
          {/* TOP HEADER */}
          <div style={{ borderBottom: '2px solid #0f172a', paddingBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '3px' }}>
                <span style={{ fontSize: '24px', fontWeight: '900', letterSpacing: '-0.03em', fontFamily: 'monospace', color: '#0f172a' }}>
                  AZURA<span style={{ color: '#0d9488' }}>OS</span>
                </span>
                <span style={{ fontSize: '9px', fontWeight: '800', fontFamily: 'monospace', padding: '2px 8px', backgroundColor: '#f0fdfa', color: '#0f766e', border: '1px solid #99f6e4', borderRadius: '4px', textTransform: 'uppercase' }}>
                  Venture One-Pager
                </span>
              </div>
              <div style={{ fontSize: '10px', fontWeight: '700', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em', fontFamily: 'monospace' }}>
                Sovereign Financial Operating Engine for Independent Operators &amp; Lean SMEs
              </div>
              <div style={{ fontSize: '10px', color: '#64748b', marginTop: '2px' }}>
                Company: <strong style={{ color: '#1e293b' }}>Azura Seraph™ Pty Ltd</strong> • Founder &amp; Architect: <strong style={{ color: '#1e293b' }}>Raksha Singh</strong> • <span style={{ color: '#0d9488', fontWeight: 'bold' }}>founder@azuraos.app</span>
              </div>
            </div>
            <div style={{ textAlign: 'right', fontSize: '10px', fontFamily: 'monospace', color: '#475569' }}>
              <div style={{ fontSize: '9px', fontWeight: '900', color: '#0f766e', textTransform: 'uppercase', letterSpacing: '0.05em' }}>CONFIDENTIAL EXECUTIVE BRIEFING</div>
              <div>Prepared for: <strong style={{ color: '#0f172a' }}>Thandiwe Maqetuka CA(SA)</strong></div>
              <div>Knife Capital &amp; Grindstone Ventures</div>
              <div style={{ fontSize: '9px', color: '#64748b' }}>September 2026 • Live Production</div>
            </div>
          </div>

          {/* STRATEGIC BADGES */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px', textAlign: 'center', fontSize: '9px', fontFamily: 'monospace', fontWeight: '700', textTransform: 'uppercase' }}>
            <div style={{ padding: '6px 8px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', color: '#334155' }}>
              <span style={{ color: '#0f766e', display: 'block', fontSize: '11px', fontWeight: '900' }}>Google Cloud</span>
              Startups Program (Start Tier)
            </div>
            <div style={{ padding: '6px 8px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', color: '#334155' }}>
              <span style={{ color: '#0f766e', display: 'block', fontSize: '11px', fontWeight: '900' }}>Zero Telemetry</span>
              AES-256-GCM Sovereign Vault
            </div>
            <div style={{ padding: '6px 8px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', color: '#334155' }}>
              <span style={{ color: '#0f766e', display: 'block', fontSize: '11px', fontWeight: '900' }}>Diligence Ready</span>
              60+ Question Grindstone Intake
            </div>
            <div style={{ padding: '6px 8px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', color: '#334155' }}>
              <span style={{ color: '#0f766e', display: 'block', fontSize: '11px', fontWeight: '900' }}>Global MoR</span>
              Dodo Payments (PCI-DSS L1)
            </div>
          </div>

          {/* 1. EXECUTIVE SUMMARY & PROBLEM */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div style={{ padding: '10px 12px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
              <div style={{ fontSize: '11px', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '0.04em', fontFamily: 'monospace', color: '#0f766e', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                <Sparkles size={13} />
                1. The Executive Thesis
              </div>
              <p style={{ color: '#334155', fontSize: '10px', lineHeight: '1.45', margin: 0 }}>
                AzuraOS replaces <strong>8 to 14 fragmented SaaS subscriptions</strong> with a single deterministic operating layer. Engineered for high-output solo operators, consulting practices, and growing tech SMEs, AzuraOS turns erratic freelancing into an institutional enterprise with automated margin defense, invoicing, and audit-ready reconciliation.
              </p>
            </div>

            <div style={{ padding: '10px 12px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
              <div style={{ fontSize: '11px', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '0.04em', fontFamily: 'monospace', color: '#b91c1c', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                <TrendingUp size={13} />
                2. The Problem: SaaS Sprawl &amp; Reactive Accounting
              </div>
              <ul style={{ color: '#334155', fontSize: '9.5px', lineHeight: '1.4', margin: 0, paddingLeft: '14px' }}>
                <li><strong>28.4% Margin Erosion:</strong> Founders spend $300–$800/mo on disconnected tools with zero unified intelligence.</li>
                <li><strong>Rearview-Mirror Bookkeeping:</strong> Traditional tools only report past transactions, leaving founders blindsided by sudden tax bills.</li>
                <li><strong>Telemetry Harvesting:</strong> Proprietary client proposals and balance sheets are stored unencrypted on corporate silos.</li>
              </ul>
            </div>
          </div>

          {/* 2. THE FINANCIAL & RECONCILIATION ENGINE (CA FOCUS) */}
          <div style={{ padding: '10px 12px', backgroundColor: '#f0fdfa', border: '1px solid #99f6e4', borderRadius: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <div style={{ fontSize: '11px', fontWeight: '900', color: '#134e4a', textTransform: 'uppercase', letterSpacing: '0.04em', fontFamily: 'monospace', display: 'flex', alignItems: 'center', gap: '5px' }}>
                <DollarSign size={13} style={{ color: '#0f766e' }} />
                3. The Financial Operating Engine &amp; AI Reconciliation Suite
              </div>
              <span style={{ fontSize: '9px', fontFamily: 'monospace', fontWeight: '800', color: '#115e59', backgroundColor: '#ccfbf1', padding: '1px 6px', borderRadius: '4px' }}>
                Auditor &amp; CA(SA) Approved Flow
              </span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', fontSize: '9.5px' }}>
              <div style={{ backgroundColor: '#ffffff', padding: '8px', borderRadius: '6px', border: '1px solid #ccfbf1' }}>
                <strong style={{ color: '#0f172a', display: 'block', fontFamily: 'monospace', fontSize: '10px', marginBottom: '2px' }}>Sub-Second Margin Radar</strong>
                <p style={{ color: '#475569', lineHeight: '1.35', margin: 0 }}>
                  Calculates net profit margins in real-time. Automatically reserves provisional income tax and VAT (SARS 15%, HMRC, IRS) <em>before</em> expenses occur.
                </p>
              </div>
              <div style={{ backgroundColor: '#ffffff', padding: '8px', borderRadius: '6px', border: '1px solid #ccfbf1' }}>
                <strong style={{ color: '#0f172a', display: 'block', fontFamily: 'monospace', fontSize: '10px', marginBottom: '2px' }}>AI Reconciliation Hub</strong>
                <p style={{ color: '#475569', lineHeight: '1.35', margin: 0 }}>
                  Dual-pass bank statement (CSV) and receipt OCR matching with confidence scores. Automatic tax categorization and anomaly detection.
                </p>
              </div>
              <div style={{ backgroundColor: '#ffffff', padding: '8px', borderRadius: '6px', border: '1px solid #ccfbf1' }}>
                <strong style={{ color: '#0f172a', display: 'block', fontFamily: 'monospace', fontSize: '10px', marginBottom: '2px' }}>1-Click Accountant Export</strong>
                <p style={{ color: '#475569', lineHeight: '1.35', margin: 0 }}>
                  Produces comprehensive ledger summaries, VAT breakdown reports, and immutable audit trails ready for annual financial statements.
                </p>
              </div>
            </div>
          </div>

          {/* 3. SECURITY, GOVERNANCE & REGULATORY MOAT */}
          <div style={{ padding: '10px 12px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
            <div style={{ fontSize: '11px', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '0.04em', fontFamily: 'monospace', color: '#065f46', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '5px' }}>
              <ShieldCheck size={13} style={{ color: '#059669' }} />
              4. Enterprise Security, Privacy &amp; Regulatory Moat
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 12px', fontSize: '9.5px', color: '#334155', lineHeight: '1.35' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                <div style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: '#059669', marginTop: '5px', flexShrink: 0 }} />
                <div><strong style={{ color: '#0f172a' }}>Client-Side AES-256-GCM Vaults:</strong> Zero corporate telemetry. Invoices, balance sheets, and client data are encrypted in the founder's browser.</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                <div style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: '#059669', marginTop: '5px', flexShrink: 0 }} />
                <div><strong style={{ color: '#0f172a' }}>POPIA (SA) &amp; GDPR (EU) Compliant:</strong> Sovereign local-first storage with instant 1-click cryptographic zeroization upon account closure.</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                <div style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: '#059669', marginTop: '5px', flexShrink: 0 }} />
                <div><strong style={{ color: '#0f172a' }}>Google Cloud Partner Advantage:</strong> Hosted on Google Cloud Run (Europe-West1), inheriting SOC 1/2/3, ISO 27001, and PCI-DSS Level 1 compliance.</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                <div style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: '#059669', marginTop: '5px', flexShrink: 0 }} />
                <div><strong style={{ color: '#0f172a' }}>Non-Custodial Regulatory Moat:</strong> Does not hold customer funds and avoids risky Open Banking credentials. 100% pure software leverage.</div>
              </div>
            </div>
          </div>

          {/* 4. BUSINESS MODEL, UNIT ECONOMICS & GRINDSTONE ALIGNMENT */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div style={{ padding: '10px 12px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
              <div style={{ fontSize: '11px', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '0.04em', fontFamily: 'monospace', color: '#4338ca', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                <Layers size={13} />
                5. Unit Economics &amp; Pricing Wedge
              </div>
              <ul style={{ color: '#334155', fontSize: '9.5px', lineHeight: '1.4', margin: 0, paddingLeft: '14px' }}>
                <li><strong>Pre-Launch Pro Wedge:</strong> $29/mo ($290/yr) 100% ungated engine following 7-day trial. Fast customer acquisition.</li>
                <li><strong>Post-Seed Price Expansion:</strong> $79–$99/mo for new cohorts as product integrations and automation mature.</li>
                <li><strong>Enterprise Cohorts:</strong> Bulk licenses for incubators, ESD supplier programs, and university innovation centers.</li>
                <li><strong>Gross Margins:</strong> &gt;90% software gross margin on serverless Cloud Run infrastructure.</li>
              </ul>
            </div>

            <div style={{ padding: '10px 12px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
              <div style={{ fontSize: '11px', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '0.04em', fontFamily: 'monospace', color: '#b45309', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                <Award size={13} />
                6. Grindstone Diligence Alignment
              </div>
              <ul style={{ color: '#334155', fontSize: '9.5px', lineHeight: '1.4', margin: 0, paddingLeft: '14px' }}>
                <li><strong>60+ Question Intake Complete:</strong> Operational metrics, target market, and governance already structured.</li>
                <li><strong>Clean IP Ownership:</strong> 100% founder-architected and engineered codebase; zero agency or outsourced liabilities.</li>
                <li><strong>Capital Efficiency:</strong> Achieved live production and enterprise certifications with near-zero historical burn.</li>
                <li><strong>African &amp; Global Scale:</strong> Native support for ZAR, USD, EUR, GBP, and localized tax systems.</li>
              </ul>
            </div>
          </div>

          {/* 5. THE ASK & MILESTONES */}
          <div style={{ padding: '10px 12px', backgroundColor: '#0f172a', color: '#ffffff', borderRadius: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #334155', paddingBottom: '6px', marginBottom: '6px' }}>
              <div>
                <span style={{ fontSize: '9px', fontFamily: 'monospace', color: '#2dd4bf', textTransform: 'uppercase', fontWeight: '800', letterSpacing: '0.05em' }}>THE STRATEGIC PRE-SEED ASK</span>
                <div style={{ fontSize: '13px', fontWeight: '900', color: '#ffffff' }}>$250,000 – $500,000 Pre-Seed Capital</div>
              </div>
              <div style={{ fontSize: '9.5px', fontFamily: 'monospace', color: '#5eead4' }}>
                High-Leverage 2-Person Strategic Core Hire
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px', fontSize: '9px' }}>
              <div style={{ padding: '6px 8px', backgroundColor: '#1e293b', borderRadius: '6px', border: '1px solid #334155' }}>
                <strong style={{ color: '#5eead4', display: 'block', fontFamily: 'monospace', marginBottom: '2px' }}>1. Capital Allocation</strong>
                <p style={{ color: '#cbd5e1', margin: 0, lineHeight: '1.3' }}>
                  Funding 2 strategic hires: Enterprise/Cohort Partnerships Lead (incubator/ESD pipelines) &amp; Full-Stack Systems Engineer.
                </p>
              </div>
              <div style={{ padding: '6px 8px', backgroundColor: '#1e293b', borderRadius: '6px', border: '1px solid #334155' }}>
                <strong style={{ color: '#5eead4', display: 'block', fontFamily: 'monospace', marginBottom: '2px' }}>2. Near-Term Milestones</strong>
                <p style={{ color: '#cbd5e1', margin: 0, lineHeight: '1.3' }}>
                  500 active paying Pro subscribers, 3 cohort enterprise pilots, and regional market validation within 6 months.
                </p>
              </div>
              <div style={{ padding: '6px 8px', backgroundColor: '#1e293b', borderRadius: '6px', border: '1px solid #334155' }}>
                <strong style={{ color: '#5eead4', display: 'block', fontFamily: 'monospace', marginBottom: '2px' }}>3. 12-Month Target</strong>
                <p style={{ color: '#cbd5e1', margin: 0, lineHeight: '1.3' }}>
                  Scale to $1.2M ARR run-rate at $79/mo expansion pricing, establishing AzuraOS as the global sovereign founder OS standard.
                </p>
              </div>
            </div>
          </div>

          {/* FOOTER & DIRECT CONTACT */}
          <div style={{ borderTop: '1px solid #cbd5e1', paddingTop: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '9.5px', color: '#64748b', fontFamily: 'monospace' }}>
            <div>
              <strong>Raksha Singh</strong> • Founder &amp; Architect • Azura Seraph™ Pty Ltd • <span style={{ color: '#0f766e', fontWeight: 'bold' }}>founder@azuraos.app</span>
            </div>
            <div>
              AzuraOS Live Production: <span style={{ color: '#0f766e', fontWeight: 'bold' }}>https://azuraos.app</span>
            </div>
          </div>
        </div>
      </div>

      {/* PRINT STYLES FOR FLAWLESS SINGLE-PAGE OUTPUT */}
      <style>{`
        @media print {
          @page {
            size: A4 portrait;
            margin: 5mm;
          }
          body {
            background: #ffffff !important;
            color: #000000 !important;
          }
          .print\\:hidden, nav, header, footer, button {
            display: none !important;
          }
          #grindstone-a4-pdf-canvas-wrapper {
            position: static !important;
            left: auto !important;
            top: auto !important;
            width: 100% !important;
            height: 100% !important;
            display: block !important;
            overflow: visible !important;
            z-index: 99999 !important;
          }
          #grindstone-a4-pdf-canvas {
            width: 100% !important;
            height: 100% !important;
            max-height: none !important;
            padding: 10px 15px !important;
            box-shadow: none !important;
            border: none !important;
          }
          #grindstone-one-pager-doc {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
}
